/**
 * Generic Screen Renderer Components
 * 
 * These components render screens dynamically based on data from the backend.
 * No hardcoded text - everything comes from the API response.
 */

import React, { useState } from 'react';
import { AEATSignInWizard } from '../../features/aeat/AEATSignInWizard';
import { isAeatAccessHelpOptionLabel, openAeatAccessHelpInNewTab } from '../../features/aeat/aeatMethodMapping';

// ============================================
// TYPES
// ============================================

export interface ShowIfCondition {
  field?: string;
  op: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'in' | 'contains' | 'exists' | 'not_exists' | 'and' | 'or' | 'not' | 'includes';
  value?: any;
  conditions?: ShowIfCondition[]; // For 'and' / 'or' operators
  condition?: ShowIfCondition; // For 'not' operator
  includes?: string; // For checking if array value includes a specific item
}

export interface Question {
  id: string;
  question_text: string;
  field_type: 'text' | 'number' | 'radio' | 'checkbox' | 'select' | 'date' | 'file' | 'currency' | 'review_display' | 'review_files' | 'add_item' | 'section_header' | 'tax_summary_section' | 'tax_calculated' | 'tax_total' | 'tax_breakdown' | 'action_buttons' | 'hidden';
  options: string[];
  required: boolean;
  placeholder?: string;
  help_text?: string;
  validation_pattern?: string;
  current_value?: any;
  show_if?: ShowIfCondition;
  source_field?: string; // For review_display fields - references the original field
  source_fields?: string[]; // For tax_calculated fields - multiple source fields to sum
  allow_delete?: boolean; // For section_header and review_files fields - whether item can be deleted
  item_template?: string; // For add_item fields - template name for items to add (e.g., "dependent")
  country?: string; // Country-specific question (US, ES)
  group_id?: string; // For grouping repeatable fields
  calculation?: 'sum' | 'subtract' | 'percentage'; // For tax_calculated fields
  highlight?: 'positive' | 'negative' | 'neutral'; // For tax_total styling
  layout?: 'full' | 'half'; // Layout width - full width or half (for two-column layouts)
}

export interface Screen {
  screen_id: string;
  screen_type: 'info' | 'question' | 'consent' | 'connection' | 'review' | 'payment';
  category: string;
  country: string;
  order: number;
  title: string;
  subtitle?: string;
  description?: string;
  paragraphs: string[];
  bullet_points: string[];
  headings: string[];
  questions: Question[];
  buttons: string[];
  has_illustration: boolean;
  illustration_type?: string;
  navigation: {
    can_go_back: boolean;
    can_go_forward: boolean;
    current_index: number;
    total_screens: number;
  };
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  /** Optional; when "center", question body copy is centered (e.g. success screens). */
  content_alignment?: 'left' | 'center';
}

interface ScreenRendererProps {
  screen: Screen;
  answers: Record<string, any>;
  onAnswerChange: (questionId: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  userCountry?: string; // The user's selected country (ES or US)
}

// ============================================
// INFO SCREEN RENDERER
// ============================================

const InfoScreenRenderer: React.FC<ScreenRendererProps> = ({
  screen,
  onNext,
  onBack,
  isSubmitting,
}) => {
  return (
    <div
      className="bg-gray-50"
      style={{ ['overflowAnchor' as any]: 'none' }}
    >
      <div className="flex items-start justify-center p-8">
        <div className="max-w-lg w-full text-center">
        {/* Illustration */}
        {screen.has_illustration && (
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-[#07A2A0]/10 flex items-center justify-center">
              <IllustrationIcon type={screen.illustration_type} />
            </div>
          </div>
        )}

        {screen.headings?.length > 0 && (
          <div className="text-4xl mb-3" aria-hidden="true">
            {screen.headings.map((h, i) => (
              <span key={i}>{i > 0 ? ' ' : ''}{h}</span>
            ))}
          </div>
        )}
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          {cleanTitle(screen.title)}
        </h1>
        
        {/* Subtitle */}
        {screen.subtitle && (
          <p className="text-gray-500 mb-8 text-center">{screen.subtitle}</p>
        )}
        
        {/* Description */}
        {screen.description && (
          <p className="text-gray-600 mb-6 text-center">{screen.description}</p>
        )}
        
        {/* Paragraphs */}
        {screen.paragraphs.length > 0 && (
          <div className="space-y-4 mb-6">
            {screen.paragraphs.filter(p => !isNavigationText(p)).map((paragraph, i) => (
              <p key={i} className="text-gray-600">{cleanParagraph(paragraph)}</p>
            ))}
          </div>
        )}
        
        {/* Bullet Points */}
        {screen.bullet_points.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <ul className="space-y-3">
              {screen.bullet_points.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[#07A2A0] mt-0.5">✓</span>
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {screen.navigation.can_go_back && (
            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <button
            onClick={onNext}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg hover:bg-[#069694] transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

// ============================================
// CONDITION EVALUATOR
// ============================================

function evaluateShowIf(condition: ShowIfCondition | undefined | any, answers: Record<string, any>): boolean {
  if (!condition) return true; // No condition means always show
  
  // Handle simple showIf format from JSON config: { field: "x", value: "y" } or { field: "x", includes: "y" }
  if (condition.field && !condition.op) {
    const actual = answers[condition.field];
    
    // Handle "includes" check for multi-select/checkbox arrays
    if (condition.includes !== undefined) {
      if (Array.isArray(actual)) {
        return actual.includes(condition.includes);
      }
      return false;
    }
    
    // Handle simple value comparison
    if (condition.value !== undefined) {
      return actual === condition.value;
    }
    
    // Handle "values" array check (field matches any of the values)
    if (condition.values !== undefined && Array.isArray(condition.values)) {
      return condition.values.includes(actual);
    }
    
    return true;
  }
  
  const { field, op, value, conditions, condition: nestedCondition, includes } = condition;
  
  // Handle logical operators first
  switch (op) {
    case 'and':
      return (conditions || []).every((c: ShowIfCondition) => evaluateShowIf(c, answers));
    case 'or':
      return (conditions || []).some((c: ShowIfCondition) => evaluateShowIf(c, answers));
    case 'not':
      return !evaluateShowIf(nestedCondition, answers);
  }
  
  // For field-based conditions
  if (!field) return true;
  const actual = answers[field];
  
  switch (op) {
    case 'eq':
      return actual === value;
    case 'neq':
      return actual !== value;
    case 'gt':
      return actual !== undefined && actual !== null && Number(actual) > value;
    case 'lt':
      return actual !== undefined && actual !== null && Number(actual) < value;
    case 'gte':
      return actual !== undefined && actual !== null && Number(actual) >= value;
    case 'lte':
      return actual !== undefined && actual !== null && Number(actual) <= value;
    case 'in':
      return Array.isArray(value) && value.includes(actual);
    case 'contains':
      // Handle both string contains and array contains
      if (Array.isArray(actual)) {
        return actual.includes(value);
      }
      return typeof actual === 'string' && actual.includes(value);
    case 'includes':
      // Check if array field includes the value
      if (Array.isArray(actual)) {
        return actual.includes(includes || value);
      }
      return false;
    case 'exists':
      return actual !== undefined && actual !== null && actual !== '';
    case 'not_exists':
      return actual === undefined || actual === null || actual === '';
    default:
      return true;
  }
}

// ============================================
// QUESTION SCREEN RENDERER
// ============================================

const QuestionScreenRenderer: React.FC<ScreenRendererProps> = ({
  screen,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting,
  userCountry,
}) => {
  // Dynamic title for "Other income" screen: use the name the user entered (other_income_describe)
  const displayTitle =
    screen.screen_id === 'other_income_custom' && answers?.other_income_describe
      ? String(answers.other_income_describe).trim() || screen.title
      : screen.title;

  // Find add_item questions to get their templates
  const addItemQuestions = screen.questions.filter(q => q.field_type === 'add_item');

  // Create duplicated questions for repeatable items (e.g., dependents)
  const expandRepeatableQuestions = (): Question[] => {
    const expanded: Question[] = [];
    const processedFields = new Set<string>();
    
    // First, identify all template patterns and their counts
    const templateCounts: Record<string, number> = {};
    for (const addItemQ of addItemQuestions) {
      const templateName = addItemQ.item_template || 'item';
      templateCounts[templateName] = answers[addItemQ.id] || 1;
    }
    
    // Separate template fields from non-template fields
    const templateFields: Question[] = [];
    const nonTemplateFields: Question[] = [];
    
    for (const question of screen.questions) {
      const templateMatch = question.id.match(/^([a-z_]+)_1_(.+)$/);
      if (templateMatch && question.field_type !== 'add_item') {
        templateFields.push(question);
      } else {
        nonTemplateFields.push(question);
      }
    }
    
    // First, add non-template questions that appear before the first template field
    // (like "Do you have dependents?" question)
    for (const question of nonTemplateFields) {
      // Check if this is the add_item button - we'll handle it with the section header
      if (question.field_type === 'add_item') continue;
      
      // Add the question
      expanded.push(question);
    }
    
    // Now add template fields grouped by instance (Dependent 1 fields, then Dependent 2 fields, etc.)
    // Get all unique template names
    const templateNames = new Set<string>();
    for (const question of templateFields) {
      const match = question.id.match(/^([a-z_]+)_1_/);
      if (match) templateNames.add(match[1]);
    }
    
    // For each template (e.g., "dependent"), create copies for each instance
    for (const templateName of templateNames) {
      const itemCount = templateCounts[templateName] || 1;
      const addItemQ = addItemQuestions.find(q => q.item_template === templateName);
      
      // Get all template fields for this template type in original order
      const fieldsForTemplate = templateFields.filter(q => q.id.startsWith(`${templateName}_1_`));
      
      // For each instance (1, 2, 3...), add all fields for that instance
      for (let idx = 1; idx <= itemCount; idx++) {
        for (const question of fieldsForTemplate) {
          const fieldSuffix = question.id.replace(`${templateName}_1_`, '');
          const newId = `${templateName}_${idx}_${fieldSuffix}`;
          
          // Skip if we've already processed this field
          if (processedFields.has(newId)) continue;
          processedFields.add(newId);
          
          const newQuestion: Question = {
            ...question,
            id: newId,
            question_text: question.field_type === 'section_header' 
              ? question.question_text.replace(/\d+/, String(idx))
              : question.question_text,
            allow_delete: question.field_type === 'section_header' && idx > 1,
            group_id: `${templateName}_${idx}`, // Group fields by instance
            // Update show_if to reference the correct index
            show_if: question.show_if ? updateShowIfIndex(question.show_if, templateName, idx) : undefined
          };
          expanded.push(newQuestion);
        }
      }
      
      // Add the add_item button after all instances (it will be rendered with first header)
      if (addItemQ) {
        expanded.push(addItemQ);
      }
    }
    
    return expanded;
  };

  // Helper to update show_if condition indices
  const updateShowIfIndex = (condition: ShowIfCondition, templateName: string, newIdx: number): ShowIfCondition => {
    if (!condition) return condition;
    
    const updated = { ...condition };
    
    if (updated.field) {
      // Replace _1_ with the new index in field references
      const fieldMatch = updated.field.match(new RegExp(`^${templateName}_(\\d+)_(.+)$`));
      if (fieldMatch) {
        updated.field = `${templateName}_${newIdx}_${fieldMatch[2]}`;
      }
    }
    
    if (updated.conditions) {
      updated.conditions = updated.conditions.map(c => updateShowIfIndex(c, templateName, newIdx));
    }
    
    if (updated.condition) {
      updated.condition = updateShowIfIndex(updated.condition, templateName, newIdx);
    }
    
    return updated;
  };

  // Get expanded questions
  const expandedQuestions = expandRepeatableQuestions();

  // Filter questions based on show_if conditions and country
  const visibleQuestions = expandedQuestions.filter(q => {
    // Check country-specific questions
    if (q.country && userCountry && q.country !== userCountry) {
      return false;
    }
    return evaluateShowIf(q.show_if, answers);
  });
  
  // Check if all visible required questions are answered (excluding non-input fields)
  const canContinue = visibleQuestions.every(q => 
    !q.required || 
    q.field_type === 'section_header' || 
    q.field_type === 'add_item' ||
    q.field_type === 'action_buttons' ||
    (answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null)
  );

  // Co-ownership validation (Figma): user % + other owners % must equal 100%. Warning: "Ownership percentages should add up to 100%. Please review."
  const isCoownershipScreen = screen.screen_id === 'co_ownership_details' || screen.screen_id.startsWith('co_ownership_details_');

  const coownershipUserPctRaw = answers.coownership_context_user_pct;
  const coownershipUserPct = coownershipUserPctRaw !== undefined && coownershipUserPctRaw !== null
    ? Number(coownershipUserPctRaw)
    : NaN;

  const coownerPctValues = Object.entries(answers)
    .filter(([k]) => /^coowner_\d+_percentage$/.test(k))
    .map(([, v]) => (v === '' || v === null || v === undefined ? NaN : Number(v)))
    .filter((n) => Number.isFinite(n));

  const coownerPctSum = coownerPctValues.reduce((acc, n) => acc + n, 0);
  const ownershipTotal = (Number.isFinite(coownershipUserPct) ? coownershipUserPct : 0) + coownerPctSum;
  const shouldCheckOwnershipTotal =
    isCoownershipScreen &&
    Number.isFinite(coownershipUserPct) &&
    (coownerPctValues.length > 0 || canContinue);

  const showOwnershipWarning =
    shouldCheckOwnershipTotal && Math.abs(ownershipTotal - 100) > 0.01;

  // When we have co-owner % but no user context %, block and prompt to go back
  const hasCoownerPctButNoUserPct =
    isCoownershipScreen &&
    !Number.isFinite(coownershipUserPct) &&
    coownerPctValues.length > 0;

  // On co-ownership screens: block Continue when total % !== 100 or when user % is missing
  const effectiveCanContinue = isCoownershipScreen && (showOwnershipWarning || hasCoownerPctButNoUserPct)
    ? false
    : canContinue;

  // Handle removing an item instance (e.g., remove Dependent 2)
  const handleRemoveItem = (templateName: string, idx: number) => {
    const addItemQ = addItemQuestions.find(q => q.item_template === templateName);
    if (!addItemQ) return;
    
    const currentCount = answers[addItemQ.id] || 1;
    if (currentCount <= 1) return;
    
    // Shift answers from higher indices down
    const newAnswers: Record<string, any> = {};
    for (let i = idx; i < currentCount; i++) {
      // Get all fields for the next index and move them to current
      const nextIdx = i + 1;
      expandedQuestions.forEach(q => {
        const match = q.id.match(new RegExp(`^${templateName}_${nextIdx}_(.+)$`));
        if (match) {
          const fieldSuffix = match[1];
          const currentKey = `${templateName}_${i}_${fieldSuffix}`;
          const nextKey = `${templateName}_${nextIdx}_${fieldSuffix}`;
          if (answers[nextKey] !== undefined) {
            newAnswers[currentKey] = answers[nextKey];
          }
        }
      });
    }
    
    // Clear the last index fields
    expandedQuestions.forEach(q => {
      const match = q.id.match(new RegExp(`^${templateName}_${currentCount}_(.+)$`));
      if (match) {
        newAnswers[q.id] = undefined;
      }
    });
    
    // Update all affected answers
    Object.entries(newAnswers).forEach(([key, value]) => {
      onAnswerChange(key, value);
    });
    
    // Decrease count
    onAnswerChange(addItemQ.id, currentCount - 1);
  };

  // Render questions with special handling for repeatable items
  const renderQuestions = () => {
    const rendered: JSX.Element[] = [];
    let i = 0;
    
    while (i < visibleQuestions.length) {
      const question = visibleQuestions[i];
      
      // Check if this is a section_header for a repeatable item (e.g., Dependent 1)
      const headerMatch = question.field_type === 'section_header' && question.id.match(/^([a-z_]+)_(\d+)_header$/);
      
      if (headerMatch) {
        const templateName = headerMatch[1];
        const itemIdx = parseInt(headerMatch[2], 10);
        const addItemQ = addItemQuestions.find(q => q.item_template === templateName);
        const itemCount = addItemQ ? (answers[addItemQ.id] || 1) : 1;
        const isFirstItem = itemIdx === 1;
        
        // Render the section header with "+ Add" button on first item (Figma style: inline with header)
        rendered.push(
          <div key={question.id} className="col-span-1 lg:col-span-2 mt-6 mb-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                {cleanParagraph(question.question_text)}
              </h3>
              <div className="flex items-center gap-4">
                {/* Remove button for items after the first */}
                {itemIdx > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(templateName, itemIdx)}
                    className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Remove
                  </button>
                )}
                {/* "+ Add Dependent" button inline with first header (per Figma) */}
                {isFirstItem && addItemQ && (
                  <button
                    type="button"
                    onClick={() => {
                      onAnswerChange(addItemQ.id, itemCount + 1);
                    }}
                    className="text-[#07A2A0] hover:text-[#069694] font-semibold text-sm transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-[#07A2A0]/10"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    + Add {capitalizeFirstLetter(templateName)}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
        i++;
      } else if (question.field_type === 'add_item') {
        // Skip rendering add_item as standalone - it's rendered inline with section header
        i++;
      } else {
        // Regular question with layout support
        const isHalfWidth = question.layout === 'half';
        const isFullWidth = question.layout === 'full' || question.field_type === 'radio' || question.field_type === 'checkbox' || question.field_type === 'action_buttons';
        
        const aeatProv = answers.aeat_import_provenance as Record<string, any> | undefined;
        const isAeatField = Boolean(aeatProv && question.id in aeatProv);
        const provEntry = isAeatField ? aeatProv![question.id] : null;
        const isConflicted = provEntry?.source === 'aeat_conflicted';
        const isUserOverridden = provEntry?.source === 'user_overridden';

        const handleChange = (val: any) => {
          onAnswerChange(question.id, val);
          if (isAeatField && !isConflicted && provEntry?.source === 'aeat') {
            onAnswerChange('aeat_import_provenance', {
              ...aeatProv,
              [question.id]: { ...provEntry, source: 'user_overridden' },
            });
          }
        };

        const handleRevert = () => {
          const originals = answers.aeat_import_original_values as Record<string, any> | undefined;
          const originalValue = originals?.[question.id];
          if (originalValue !== undefined) {
            onAnswerChange(question.id, originalValue);
          }
          onAnswerChange('aeat_import_provenance', {
            ...aeatProv,
            [question.id]: { ...provEntry, source: 'aeat' },
          });
        };

        rendered.push(
          <div
            key={question.id}
            className={
              isFullWidth
                ? 'col-span-1 lg:col-span-2'
                : isHalfWidth
                  ? 'col-span-1 lg:col-span-1'
                  : 'col-span-1 lg:col-span-2'
            }
          >
            <QuestionField
              question={question}
              value={answers[question.id]}
              onChange={handleChange}
              isAeatPrefilled={isAeatField && !isConflicted}
              isAeatConflicted={isConflicted}
              isUserOverridden={isUserOverridden}
              onRevert={isUserOverridden ? handleRevert : undefined}
            />
          </div>
        );
        i++;
      }
    }
    
    return rendered;
  };

  const isCentered = screen.content_alignment === 'center';
  const footerLabels =
    Array.isArray(screen.buttons) && screen.buttons.length > 0
      ? screen.buttons
      : ['Back', 'Continue'];

  return (
    <div
      className="bg-gray-50"
      style={{ overflowAnchor: 'none' }}
    >
      <div className={`max-w-2xl mx-auto p-8${isCentered ? ' text-center' : ''}`}>
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {cleanTitle(displayTitle)}
        </h1>
        
        {/* Subtitle */}
        {screen.subtitle && (
          <p
            className={
              isCentered
                ? 'text-gray-600 mb-6 whitespace-pre-line'
                : 'text-gray-500 mb-8'
            }
          >
            {screen.subtitle}
          </p>
        )}

        {screen.description?.trim() ? (
          <p className="text-gray-600 mb-8 whitespace-pre-line">{screen.description}</p>
        ) : null}
        
        {/* Questions - Grid layout for two-column support */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-6 mb-8">
          {renderQuestions()}
        </div>

        {showOwnershipWarning && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            Ownership percentages should add up to 100%. Please review.
          </div>
        )}
        {hasCoownerPctButNoUserPct && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            Your ownership percentage could not be loaded. Please go back to the previous screen and continue again.
          </div>
        )}
        
        {/* Navigation Buttons — custom labels when screen.buttons is set; default preserves Back + Continue */}
        <div className={`flex gap-4 flex-wrap${isCentered ? ' justify-center' : ''}`}>
          {footerLabels.map((label, idx) => {
            if (label === 'Back') {
              if (!screen.navigation.can_go_back) return null;
              return (
                <button
                  key={`footer-${idx}-back`}
                  type="button"
                  onClick={onBack}
                  disabled={isSubmitting}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
              );
            }
            if (label === 'Contact us') {
              return (
                <button
                  key={`footer-${idx}-contact`}
                  type="button"
                  onClick={() => window.open('https://www.innotaxes.com/contact', '_blank', 'noopener,noreferrer')}
                  className="px-6 py-3 border-2 border-[#07A2A0] text-[#07A2A0] font-semibold rounded-lg hover:bg-[#07A2A0]/10 transition-colors"
                >
                  Contact us
                </button>
              );
            }
            const isPrimaryContinue =
              label === 'Continue' ||
              label === 'Everything is correct' ||
              label === 'Approve and continue';
            return (
              <button
                key={`footer-${idx}-${label}`}
                type="button"
                onClick={onNext}
                disabled={(!effectiveCanContinue && isPrimaryContinue) || isSubmitting}
                className={
                  isPrimaryContinue
                    ? `flex-1 px-6 py-3 font-semibold rounded-lg transition-colors ${
                        effectiveCanContinue && !isSubmitting
                          ? 'bg-[#07A2A0] text-white hover:bg-[#069694]'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`
                    : 'px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors'
                }
              >
                {isSubmitting && isPrimaryContinue ? 'Loading...' : label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Helper function to capitalize first letter
function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}

// ============================================
// CONSENT SCREEN RENDERER
// ============================================

const ConsentScreenRenderer: React.FC<ScreenRendererProps> = ({
  screen,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting,
}) => {
  const consentKey = `consent_${screen.screen_id}`;
  const isAgreed = answers[consentKey] === true;

  return (
    <div
      className="bg-gray-50"
      style={{ ['overflowAnchor' as any]: 'none' }}
    >
      <div className="flex items-start justify-center p-8">
        <div className="max-w-lg w-full">
        {/* Illustration */}
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 rounded-full bg-[#07A2A0]/10 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
          </div>
        </div>
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          {cleanTitle(screen.title)}
        </h1>
        
        {/* Subtitle */}
        {screen.subtitle && (
          <p className="text-gray-500 mb-8 text-center">{screen.subtitle}</p>
        )}
        
        {/* Consent text from paragraphs */}
        {screen.paragraphs.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            {screen.paragraphs.filter(p => !isNavigationText(p) && !isConsentCheckbox(p)).map((paragraph, i) => (
              <p key={i} className="text-sm text-gray-600 mb-2">{cleanParagraph(paragraph)}</p>
            ))}
          </div>
        )}
        
        {/* Consent Checkbox - label click handled here to avoid outer scroll (sr-only input focus) */}
        <label
          className="flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer mb-8 hover:border-gray-300 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            onAnswerChange(consentKey, !isAgreed);
          }}
        >
          <div className="mt-0.5">
            <input
              type="checkbox"
              checked={isAgreed}
              tabIndex={-1}
              onChange={() => onAnswerChange(consentKey, !isAgreed)}
              className="sr-only"
              aria-hidden
            />
            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
              isAgreed ? 'bg-[#07A2A0] border-[#07A2A0]' : 'border-gray-300'
            }`}>
              {isAgreed && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-900 font-medium">
              {getConsentText(screen.paragraphs) || "Yes, I agree to the terms and conditions."}
            </span>
          </div>
        </label>
        
        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {screen.navigation.can_go_back && (
            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <button
            onClick={onNext}
            disabled={!isAgreed || isSubmitting}
            className={`flex-1 px-6 py-3 font-semibold rounded-lg transition-colors ${
              isAgreed && !isSubmitting
                ? 'bg-[#07A2A0] text-white hover:bg-[#069694]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

// ============================================
// REVIEW SCREEN RENDERER
// ============================================

const ReviewScreenRenderer: React.FC<ScreenRendererProps> = ({
  screen,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting,
}) => {
  // Filter questions based on show_if conditions
  const visibleQuestions = screen.questions.filter(q => evaluateShowIf(q.show_if, answers));
  
  // Separate display fields from file fields and tax summary fields
  const displayFields = visibleQuestions.filter(q => q.field_type === 'review_display');
  const fileFields = visibleQuestions.filter(q => q.field_type === 'review_files');
  const taxFields = visibleQuestions.filter(q => 
    ['tax_summary_section', 'tax_calculated', 'tax_total', 'tax_breakdown'].includes(q.field_type)
  );
  const isTaxSummary = taxFields.length > 0;

  // Calculate value for tax_calculated / tax_total / tax_breakdown fields
  const calculateFieldValue = (question: Question): number | string => {
    // Prefer backend-injected value (e.g. from tax calculation) when present
    const injected = answers[question.id];
    if (injected !== undefined && injected !== null && injected !== '') {
      if (typeof injected === 'number' && !Number.isNaN(injected)) return injected;
      if (typeof injected === 'string' && injected.endsWith('%')) return injected;
      const n = parseFloat(String(injected));
      if (!Number.isNaN(n)) return n;
    }
    if (question.source_fields && question.source_fields.length > 0) {
      const values = question.source_fields.map(field => {
        const val = answers[field];
        return typeof val === 'number' ? val : (parseFloat(val) || 0);
      });
      if (question.calculation === 'subtract') {
        return values.reduce((a, b, idx) => idx === 0 ? b : a - b, 0);
      }
      return values.reduce((a, b) => a + b, 0);
    }
    if (question.source_field) {
      const val = answers[question.source_field];
      return typeof val === 'number' ? val : (parseFloat(val) || 0);
    }
    return 0;
  };

  // Group display fields by category (based on field name patterns)
  const groupDisplayFields = () => {
    const groups: { title: string; fields: Question[]; editTarget?: string }[] = [];
    
    // Define field groups with their corresponding edit screens
    const fieldGroups = [
      { title: 'Personal Information', patterns: ['first_name', 'middle_name', 'surname', 'date_of_birth', 'occupation'], editTarget: 'personal_details' },
      { title: 'Address', patterns: ['street', 'apt', 'city', 'province', 'postal', 'country'], editTarget: 'address_details' },
      { title: 'Tax Identification', patterns: ['tax_id'], editTarget: 'tax_identification' },
      { title: 'Disability Status', patterns: ['disability'], editTarget: 'disability_status' },
      { title: 'Marital Status', patterns: ['marital'], editTarget: 'marital_status' },
    ];
    
    const assignedFields = new Set<string>();
    
    for (const group of fieldGroups) {
      const matchingFields = displayFields.filter(q => {
        const sourceField = q.source_field || q.id;
        return group.patterns.some(pattern => 
          sourceField.toLowerCase().includes(pattern.toLowerCase())
        ) && !assignedFields.has(q.id);
      });
      
      // Only add groups that have fields with values
      // For review_display fields, current_value comes from backend (already resolved from source_field)
      const fieldsWithValues = matchingFields.filter(q => {
        const fieldValue = q.current_value !== undefined ? q.current_value : (q.source_field ? answers[q.source_field] : answers[q.id]);
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      });
      
      if (fieldsWithValues.length > 0) {
        matchingFields.forEach(f => assignedFields.add(f.id));
        groups.push({ title: group.title, fields: matchingFields, editTarget: group.editTarget });
      }
    }
    
    // Add remaining ungrouped fields
    const remainingFields = displayFields.filter(q => !assignedFields.has(q.id));
    const remainingWithValues = remainingFields.filter(q => {
      const fieldValue = q.current_value !== undefined ? q.current_value : (q.source_field ? answers[q.source_field] : answers[q.id]);
      return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
    });
    
    if (remainingWithValues.length > 0) {
      groups.push({ title: 'Other Information', fields: remainingFields });
    }
    
    return groups;
  };

  const fieldGroups = groupDisplayFields();
  
  return (
    <div
      className="bg-gray-50"
      style={{ ['overflowAnchor' as any]: 'none' }}
    >
      <div className="max-w-2xl mx-auto p-8">
        {/* Title - Figma style */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {cleanTitle(screen.title)}
        </h1>
        
        {/* Subtitle */}
        {screen.subtitle && (
          <p className="text-gray-500 mb-6">{screen.subtitle}</p>
        )}

        {/* Tax Summary Display */}
        {isTaxSummary && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            {taxFields.map((question) => {
              const fieldValue = calculateFieldValue(question);
              return (
                <QuestionField
                  key={question.id}
                  question={question}
                  value={fieldValue}
                  onChange={(value) => onAnswerChange(question.id, value)}
                />
              );
            })}
          </div>
        )}
        
        {/* Review Display Fields - Figma-style form layout */}
        {fieldGroups.length > 0 && (
          <div className="space-y-8 mb-6">
            {fieldGroups.map((group, idx) => (
              <div key={idx}>
                {/* Section Header with Edit link */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{group.title}</h3>
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-[#07A2A0] hover:text-[#069694] font-medium transition-colors"
                  >
                    Edit
                  </button>
                </div>
                
                {/* Fields in Figma-style two-column grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
                  {group.fields.map((question) => {
                    // For review_display, use current_value from backend (resolved from source_field)
                    const fieldValue = question.current_value !== undefined 
                      ? question.current_value 
                      : (question.source_field ? answers[question.source_field] : answers[question.id]);
                    
                    // Only show fields that have values
                    if (!fieldValue && fieldValue !== 0) return null;
                    
                    return (
                      <div key={question.id}>
                        <label className="block text-sm text-gray-600 mb-1">
                          {cleanParagraph(question.question_text)}
                          {question.required && <span className="text-red-500">*</span>}
                        </label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                          {formatDisplayValue(fieldValue) || <span className="text-gray-400">—</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Uploaded Files Section - Figma style */}
        {fileFields.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Uploaded Files:</h3>
            {fileFields.map((question) => (
              <QuestionField
                key={question.id}
                question={question}
                value={question.source_field ? answers[question.source_field] : answers[question.id]}
                onChange={(value) => onAnswerChange(question.source_field || question.id, value)}
              />
            ))}
          </div>
        )}
        
        {/* Navigation Buttons - Figma style */}
        <div className="flex gap-4 mt-8">
          {screen.navigation.can_go_back && (
            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <button
            onClick={onNext}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg hover:bg-[#069694] transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Loading...' : (screen.buttons.includes('Everything is correct') ? 'Everything is correct' : 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// CONNECTION SCREEN RENDERER
// ============================================

const ConnectionScreenRenderer: React.FC<ScreenRendererProps> = ({
  screen,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting,
}) => {
  if (screen.screen_id === 'sign_in_to_aeat') {
    return (
      <AEATSignInWizard
        screen={screen}
        answers={answers}
        onAnswerChange={onAnswerChange}
        onNext={onNext}
        onBack={onBack}
        isSubmitting={isSubmitting}
      />
    );
  }

  const connectionKey = `connection_${screen.screen_id}`;
  const questionKey = screen.questions?.[0]?.id || connectionKey;
  const selectedMethod = answers[questionKey] ?? answers[connectionKey];
  
  // Extract connection options from paragraphs or questions
  const connectionOptions = extractConnectionOptions(screen);

  return (
    <div
      className="bg-gray-50"
      style={{ ['overflowAnchor' as any]: 'none' }}
    >
      <div className="flex items-start justify-center p-8">
        <div className="max-w-lg w-full">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          {cleanTitle(screen.title)}
        </h1>
        
        {/* Subtitle */}
        {screen.subtitle && (
          <p className="text-gray-500 mb-8 text-center">{screen.subtitle}</p>
        )}
        
        {/* Question text if exists */}
        {screen.questions.length > 0 && (
          <p className="text-gray-700 font-medium mb-4">
            {cleanParagraph(screen.questions[0].question_text)}
          </p>
        )}
        
        {/* Connection Options */}
        {connectionOptions.length > 0 && (
          <div className="space-y-3 mb-6">
            {connectionOptions.map((option, i) => (
              <label
                key={i}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedMethod === option
                    ? 'border-[#07A2A0] bg-[#07A2A0]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  onAnswerChange(questionKey, option);
                  if (questionKey !== connectionKey) {
                    onAnswerChange(connectionKey, option);
                  }
                }}
              >
                <input
                  type="radio"
                  name="connectionMethod"
                  checked={selectedMethod === option}
                  tabIndex={-1}
                  onChange={() => {
                    onAnswerChange(questionKey, option);
                    if (questionKey !== connectionKey) {
                      onAnswerChange(connectionKey, option);
                    }
                  }}
                  className="sr-only"
                  aria-hidden
                />
                <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                  selectedMethod === option ? 'border-[#07A2A0]' : 'border-gray-300'
                }`}>
                  {selectedMethod === option && (
                    <div className="w-3 h-3 rounded-full bg-[#07A2A0]" />
                  )}
                </div>
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}
        
        {/* Info text from paragraphs */}
        {screen.paragraphs.filter(p => isInfoText(p)).length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-800">
              {screen.paragraphs.filter(p => isInfoText(p)).map(cleanParagraph).join(' ')}
            </p>
          </div>
        )}
        
        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {screen.navigation.can_go_back && (
            <button
              onClick={onBack}
              disabled={isSubmitting}
              className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <button
            onClick={onNext}
            disabled={(connectionOptions.length > 0 && !selectedMethod) || isSubmitting}
            className={`flex-1 px-6 py-3 font-semibold rounded-lg transition-colors ${
              (connectionOptions.length === 0 || selectedMethod) && !isSubmitting
                ? 'bg-[#07A2A0] text-white hover:bg-[#069694]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Loading...' : 'Continue'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

// ============================================
// QUESTION FIELD COMPONENT
// ============================================

interface QuestionFieldProps {
  question: Question;
  value: any;
  onChange: (value: any) => void;
  isAeatPrefilled?: boolean;
  isAeatConflicted?: boolean;
  isUserOverridden?: boolean;
  onRevert?: () => void;
}

const ConflictBadge: React.FC = () => (
  <span className="inline-flex items-center ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 align-middle" title="AEAT value differs from your input">
    Review
  </span>
);

const AeatRevertButton: React.FC<{ onRevert: () => void }> = ({ onRevert }) => (
  <button
    type="button"
    onClick={onRevert}
    className="inline-flex items-center ml-1.5 p-0.5 rounded text-amber-600 hover:text-amber-700 hover:bg-amber-100 transition-colors align-middle"
    title="Revert to AEAT imported value"
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
    </svg>
  </button>
);

const QuestionField: React.FC<QuestionFieldProps> = ({ question, value, onChange, isAeatPrefilled, isAeatConflicted, isUserOverridden, onRevert }) => {
  const [fileValue, setFileValue] = useState<File | null>(null);

  switch (question.field_type) {
    case 'radio':
      return (
        <div className={isAeatPrefilled ? 'border-l-2 border-blue-400 pl-2' : ''}>
          <label className="block text-base font-semibold text-gray-900 mb-4">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <div className="space-y-2">
            {question.options.map((option) => (
              <label
                key={option}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  value === option
                    ? 'border-[#07A2A0] bg-[#07A2A0]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  if (isAeatAccessHelpOptionLabel(option)) {
                    openAeatAccessHelpInNewTab();
                    return;
                  }
                  onChange(option);
                }}
              >
                <input
                  type="radio"
                  name={question.id}
                  checked={value === option}
                  tabIndex={-1}
                  onChange={() => {
                    if (isAeatAccessHelpOptionLabel(option)) {
                      openAeatAccessHelpInNewTab();
                      return;
                    }
                    onChange(option);
                  }}
                  className="sr-only"
                  aria-hidden
                />
                <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${
                  value === option ? 'border-[#07A2A0]' : 'border-gray-300'
                }`}>
                  {value === option && (
                    <div className="w-3 h-3 rounded-full bg-[#07A2A0]" />
                  )}
                </div>
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        </div>
      );

    case 'checkbox':
      return (
        <div className={isAeatPrefilled ? 'border-l-2 border-blue-400 pl-2' : ''}>
          <label className="block text-base font-semibold text-gray-900 mb-4">
            {cleanParagraph(question.question_text)}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          <div className="space-y-2">
            {question.options.map((option) => {
              const isChecked = Array.isArray(value) && value.includes(option);
              return (
                <label
                  key={option}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isChecked
                      ? 'border-[#07A2A0] bg-[#07A2A0]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    const current = Array.isArray(value) ? value : [];
                    const updated = isChecked
                      ? current.filter(v => v !== option)
                      : [...current, option];
                    onChange(updated);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    tabIndex={-1}
                    onChange={() => {
                      const current = Array.isArray(value) ? value : [];
                      const updated = isChecked
                        ? current.filter(v => v !== option)
                        : [...current, option];
                      onChange(updated);
                    }}
                    className="sr-only"
                    aria-hidden
                  />
                  <div className={`w-5 h-5 rounded border-2 mr-4 flex items-center justify-center ${
                    isChecked ? 'bg-[#07A2A0] border-[#07A2A0]' : 'border-gray-300'
                  }`}>
                    {isChecked && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-900">{option}</span>
                </label>
              );
            })}
          </div>
        </div>
      );

    case 'select':
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#07A2A0] focus:border-transparent bg-white ${isAeatConflicted || isUserOverridden ? 'border-amber-300 bg-amber-50/30' : isAeatPrefilled ? 'border-blue-200' : 'border-gray-300'}`}
          >
            <option value="">Select an option</option>
            {question.options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );

    case 'number':
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={question.placeholder}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#07A2A0] focus:border-transparent ${isAeatConflicted || isUserOverridden ? 'border-amber-300 bg-amber-50/30' : isAeatPrefilled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-300'}`}
          />
        </div>
      );

    case 'date':
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#07A2A0] focus:border-transparent ${isAeatConflicted || isUserOverridden ? 'border-amber-300 bg-amber-50/30' : isAeatPrefilled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-300'}`}
          />
        </div>
      );

    case 'file':
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg className="w-8 h-8 mb-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {fileValue ? (
                <p className="text-sm text-[#07A2A0] font-medium">{fileValue.name}</p>
              ) : (
                <p className="text-sm text-gray-500">Click to upload file</p>
              )}
            </div>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFileValue(file);
                onChange(file?.name || null);
              }}
              className="hidden"
            />
          </label>
        </div>
      );

    case 'currency':
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
              placeholder={question.placeholder || "0.00"}
              className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#07A2A0] focus:border-transparent ${isAeatConflicted || isUserOverridden ? 'border-amber-300 bg-amber-50/30' : isAeatPrefilled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-300'}`}
            />
          </div>
        </div>
      );

    case 'action_buttons':
      return (
        <div className="w-full">
          {question.question_text?.trim() ? (
            <p className="text-sm font-semibold text-gray-900 mb-3">{cleanParagraph(question.question_text)}</p>
          ) : null}
          <div className="space-y-3">
            {question.options.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => onChange(label)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-gray-900 font-medium hover:border-[#07A2A0] hover:bg-[#07A2A0]/5 transition-colors flex items-center justify-center gap-2"
              >
                <span>{label}</span>
                <svg className="w-5 h-5 text-[#07A2A0] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      );

    case 'review_display':
      // Read-only display for review screens showing previously collected data
      return (
        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600">{cleanParagraph(question.question_text)}</span>
          <span className="text-gray-900 font-medium">
            {formatDisplayValue(value) || <span className="text-gray-400 italic">Not provided</span>}
          </span>
        </div>
      );

    case 'tax_summary_section':
      // Section header for tax summary sections (Income, Deductions, Tax Liability)
      return (
        <div className="mt-6 mb-4">
          <h3 className="text-lg font-bold text-gray-900 pb-2 border-b-2 border-[#07A2A0]">
            {cleanParagraph(question.question_text)}
          </h3>
        </div>
      );

    case 'tax_calculated':
      // Calculated field showing sum or computed values from source_fields
      return (
        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600">{cleanParagraph(question.question_text)}</span>
          <span className="text-gray-900 font-medium">
            {formatCurrency(value) || '€0.00'}
          </span>
        </div>
      );

    case 'tax_total':
      // Highlighted total row (e.g., Tax Due, Refund Amount)
      const isPositive = question.highlight === 'positive';
      const isNegative = question.highlight === 'negative';
      return (
        <div className={`flex justify-between items-center py-4 px-4 rounded-lg mt-4 ${
          isPositive ? 'bg-green-50' : isNegative ? 'bg-red-50' : 'bg-gray-100'
        }`}>
          <span className={`font-semibold ${
            isPositive ? 'text-green-800' : isNegative ? 'text-red-800' : 'text-gray-900'
          }`}>
            {cleanParagraph(question.question_text)}
          </span>
          <span className={`text-xl font-bold ${
            isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-900'
          }`}>
            {formatCurrency(value) || '€0.00'}
          </span>
        </div>
      );

    case 'hidden':
      return null;

    case 'tax_breakdown':
      // Detailed breakdown row with multiple values
      return (
        <div className="flex justify-between items-center py-2 pl-4 border-b border-gray-50">
          <span className="text-sm text-gray-500">{cleanParagraph(question.question_text)}</span>
          <span className="text-sm text-gray-700">
            {formatCurrency(value) || '€0.00'}
          </span>
        </div>
      );

    case 'review_files':
      // Display uploaded files with delete option for review screens
      const files = Array.isArray(value) ? value : (value ? [value] : []);
      return (
        <div className="mt-6">
          <label className="block text-base font-semibold text-gray-900 mb-3">
            {cleanParagraph(question.question_text)}
          </label>
          {files.length > 0 ? (
            <div className="space-y-2">
              {files.map((file: string, idx: number) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-gray-700">{file}</span>
                  </div>
                  {question.allow_delete && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedFiles = files.filter((_: string, i: number) => i !== idx);
                        onChange(updatedFiles.length > 0 ? updatedFiles : null);
                      }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic">No files uploaded</p>
          )}
        </div>
      );

    case 'add_item':
      // Button to add more items (e.g., dependents, properties) - rendered inline with section header
      return null; // Rendered as part of section_header_with_add

    case 'section_header':
      // Section header for grouping fields (e.g., "Dependent 1")
      return (
        <div className="flex items-center justify-between mt-6 mb-4 pb-2 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {cleanParagraph(question.question_text)}
          </h3>
          {question.allow_delete && (
            <button
              type="button"
              onClick={() => onChange('delete')}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
            >
              Remove
            </button>
          )}
        </div>
      );

    case 'text':
    default:
      return (
        <div>
          <label className="block text-base font-semibold text-gray-900 mb-2">
            {cleanParagraph(question.question_text)}
            {question.required && <span className="text-red-500 ml-1">*</span>}
            
            {isUserOverridden && onRevert && <AeatRevertButton onRevert={onRevert} />}
            {isAeatConflicted && <ConflictBadge />}
          </label>
          {question.help_text && (
            <p className="text-sm text-gray-500 mb-3">{question.help_text}</p>
          )}
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-[#07A2A0] focus:border-transparent ${isAeatConflicted || isUserOverridden ? 'border-amber-300 bg-amber-50/30' : isAeatPrefilled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-300'}`}
          />
        </div>
      );
  }
};

// ============================================
// ILLUSTRATION ICON COMPONENT
// ============================================

const IllustrationIcon: React.FC<{ type?: string }> = ({ type }) => {
  switch (type) {
    case 'couple_illustration':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      );
    case 'process_steps_icon':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
          <rect x="9" y="3" width="6" height="4" rx="1"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
      );
    case 'checklist_icon':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <path d="M9 11l3 3L22 4"/>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
        </svg>
      );
    case 'clock_icon':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      );
    case 'price_tag_icon':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
        </svg>
      );
    case 'confetti_icon':
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <path d="M12 3v4M4.8 6.8l2.8 2.8M3 12h4M4.8 17.2l2.8-2.8M12 17v4M19.2 17.2l-2.8-2.8M17 12h4M19.2 6.8l-2.8 2.8" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M9 20l-1.5 1M15 20l1.5 1M20 9l1-1.5M4 9L3 7.5" />
        </svg>
      );
    default:
      return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#07A2A0" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4M12 8h.01"/>
        </svg>
      );
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function formatDisplayValue(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'number') return formatCurrency(value);
  return String(value);
}

function formatCurrency(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}

function cleanTitle(title: string): string {
  // Remove navigation prefixes like "Welcome & Setup    ", "Onboarding    "
  // Don't strip "Income" when it's part of "Income by X", "Income from X", or "Income Details" (e.g. "Income by Location", "Income from Spain", "Income Details")
  return title
    .replace(/^(Welcome & Setup|Onboarding|Year & Residency|Consent & Access|My Info|Income(?!\s+by\s)(?!\s+from\s)(?!\s+Details)|Deductions|Foreign Reporting|Non-Resident|Payments|Review & Submit|Documents|Support|Profile)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanParagraph(text: string): string {
  // Remove navigation/menu text
  return text
    .replace(/^(Welcome & Setup|Onboarding|Year & Residency|Consent & Access|My Info|Income|Deductions|Foreign Reporting|Non-Resident|Payments|Review & Submit|Documents|Support|Profile)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isNavigationText(text: string): boolean {
  const navPatterns = ['Back', 'Continue', 'Submit', 'Next', 'Previous'];
  return navPatterns.some(pattern => text.trim().toLowerCase() === pattern.toLowerCase());
}

function isConsentCheckbox(text: string): boolean {
  return text.toLowerCase().includes('yes, i agree') || text.toLowerCase().includes('i authorize');
}

function isInfoText(text: string): boolean {
  return text.toLowerCase().includes('fetches tax data') || 
         text.toLowerCase().includes('never sees credentials') ||
         text.toLowerCase().includes('secure');
}

function getConsentText(paragraphs: string[]): string {
  const consentParagraph = paragraphs.find(p => 
    p.toLowerCase().includes('yes, i agree') || p.toLowerCase().includes('i authorize')
  );
  return consentParagraph ? cleanParagraph(consentParagraph) : '';
}

function extractConnectionOptions(screen: Screen): string[] {
  // Prefer explicit options from the screen definition (backend JSON).
  // Fall back to paragraph-based extraction for legacy connection screens.
  const firstQuestion = screen.questions?.[0] as any;
  const explicit = Array.isArray(firstQuestion?.options) ? [...firstQuestion.options] : [];
  if (explicit.length > 0) {
    if (screen.screen_id === 'sign_in_to_aeat') {
      return explicit.filter((o: string) => !String(o).includes('IBAN'));
    }
    return explicit;
  }

  // Try to extract options from paragraphs (legacy)
  const options: string[] = [];
  
  const optionPatterns = [
    'Digital Certificate / DNIe',
    'Cl@ve PIN / Cl@ve Móvil',
    'Número de referencia',
    'IBAN (if no prior return filed)',
  ];
  
  for (const pattern of optionPatterns) {
    if (screen.paragraphs.some(p => p.includes(pattern))) {
      options.push(pattern);
    }
  }
  
  return options;
}

// ============================================
// MAIN SCREEN RENDERER EXPORT
// ============================================

export const ScreenRenderer: React.FC<ScreenRendererProps> = (props) => {
  switch (props.screen.screen_type) {
    case 'info':
      return <InfoScreenRenderer {...props} />;
    case 'question':
      return <QuestionScreenRenderer {...props} />;
    case 'consent':
      return <ConsentScreenRenderer {...props} />;
    case 'connection':
      return <ConnectionScreenRenderer {...props} />;
    case 'review':
      return <ReviewScreenRenderer {...props} />;
    case 'payment':
      return <InfoScreenRenderer {...props} />;
    default:
      return <InfoScreenRenderer {...props} />;
  }
};

export default ScreenRenderer;
